import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';
import { updateTicketActivity } from './ticketActivity';
import { getCachedUserAndProfile } from '../utils/userCache';

const BUCKET = 'ticket-attachments';

/**
 * 取得 Ticket 的所有附件
 */
export const getAttachmentsByTicket = async (ticketId) => {
  try {
    const { data, error } = await supabase
      .from('ticket_attachments')
      .select(`
        *,
        uploader:uploaded_by (id, email)
      `)
      .eq('ticket_id', ticketId)
      .is('delivery_log_id', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching attachments:', error);
    return { data: null, error };
  }
};

/**
 * 上傳附件
 * @param {number} ticketId
 * @param {string} uploadedBy - user UUID
 * @param {File} file
 */
export const uploadAttachment = async (ticketId, uploadedBy, file) => {
  try {
    // Generate unique file path
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${ticketId}/${timestamp}_${safeName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    // Create DB record
    const { data: attachment, error: dbError } = await supabase
      .from('ticket_attachments')
      .insert({
        ticket_id: ticketId,
        uploaded_by: uploadedBy,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type,
      })
      .select(`
        *,
        uploader:uploaded_by (id, email)
      `)
      .single();

    if (dbError) throw dbError;

    // Audit log
    const { data: ticketMeta } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', ticketId)
      .single();
    logAudit('create', 'ticket_attachment', attachment.id, file.name, {
      after: { file_name: file.name, file_path: filePath, ticket_id: ticketId, uploaded_by: uploadedBy },
    }, { ticket_id: ticketId },
      ticketMeta?.department_id,
      ticketMeta?.company_id
    );

    await updateTicketActivity(ticketId, uploadedBy);

    return { data: attachment, error: null };
  } catch (error) {
    console.error('Error uploading attachment:', error);
    return { data: null, error };
  }
};

/**
 * 取得附件下載 URL
 */
export const getAttachmentUrl = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, 3600); // 1 hour

    if (error) throw error;
    return { data: data.signedUrl, error: null };
  } catch (error) {
    console.error('Error getting attachment URL:', error);
    return { data: null, error };
  }
};

/**
 * 刪除附件
 */
export const deleteAttachment = async (attachmentId, filePath) => {
  try {
    // 先取得附件資訊用於日誌
    const { data: attachData } = await supabase
      .from('ticket_attachments')
      .select('id, ticket_id, file_name, file_path, uploaded_by, tickets:ticket_id(company_id, department_id)')
      .eq('id', attachmentId)
      .single();

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([filePath]);

    if (storageError) {
      console.warn('Storage deletion warning:', storageError);
    }

    // Delete DB record
    const { error: dbError } = await supabase
      .from('ticket_attachments')
      .delete()
      .eq('id', attachmentId);

    if (dbError) throw dbError;

    await logAudit('delete', 'ticket_attachment', attachmentId, attachData?.file_name, {
      before: { file_name: attachData?.file_name, file_path: attachData?.file_path, ticket_id: attachData?.ticket_id, uploaded_by: attachData?.uploaded_by },
    }, { ticket_id: attachData?.ticket_id },
      attachData?.tickets?.department_id,
      attachData?.tickets?.company_id
    );

    const { profile } = await getCachedUserAndProfile(supabase);
    await updateTicketActivity(attachData?.ticket_id, profile?.id);

    return { error: null };
  } catch (error) {
    console.error('Error deleting attachment:', error);
    return { error };
  }
};
